// B-92 — "Configuración de alertas", from the Alertas screen: who gets the client's alert emails.
//
// Two cards side by side, both the client's (not one office's):
//  - **Resumen diario** — the day's email (B-71): the close of the day with the Matriz.
//  - **Aviso inmediato** — an email of its own, sent the moment a grave difference is found. Always
//    on: what the person sets is the amount each difference has to pass (for Bancamía caja and
//    bóveda, 5.000.000 by default). The same amount raises the panel's "Diferencia grave", because
//    the backend stores it where the closing is evaluated.
// Pausing a client's emails is not offered here: it stays with Mantis, from the backoffice, and a
// save from this dialog never sends `enabled`, so it can neither pause nor resume a client.
//
// Each card holds its addresses as chips (✕ to take one off) under a field that adds one or more
// ("Agregar" or Enter). Adding, removing and the amounts change only the screen; "Guardar" sends it
// all at once, and an address typed and not added is still saved. Help text is kept to one line per
// card: the screen should read as the lists and the amounts, not as an explanation of them — what
// each email carries and when it goes is behind the (i) beside the card's title.
//
// The urgent email also takes the client's own message, drawn at its top (`urgent.message`), and
// "Vista previa" shows it as a recipient would read it: the backend's own email over an example
// office (`previewCierreUrgentEmail`), with the message as it is typed, saved or not.
//
// Read by anyone who sees the client (`getCierreNotificationSettings`); saved with
// `cierres.notifications.manage` on the whole client (`saveCierreNotificationSettings`) — the
// server's `canEdit` decides whether the dialog is a form or a reading.
import { useEffect, useState } from "react";
import { Eye, Info, Mail, Plus, X } from "lucide-react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Spinner,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from "@mantis-sas/ui";

import {
  getCierreNotificationSettings,
  previewCierreUrgentEmail,
  saveCierreNotificationSettings,
} from "../../../ApiCalls/mantisBusiness";
import { correosEscritos } from "../../../utils/cierreConsolidado";
import { correctionError } from "../../../utils/cierreCorrections";
import { guia } from "../../../utils/cierreTours";
import { BotonGuia } from "./GuiaPantalla";

const errorDe = (response, porDefecto) =>
  response?.errorCode || response?.message ? correctionError(response) : porDefecto;

const INPUT =
  "h-9 min-w-0 flex-1 rounded-[8px] border bg-card px-2.5 text-[12.5px] text-brinks-foreground outline-none focus:border-brinks-border-strong disabled:opacity-70";

/** `5000000` -> `5.000.000`, the way the panel writes pesos. */
const formatoPesos = (valor) =>
  valor === null || valor === undefined || valor === "" ? "" : Number(valor).toLocaleString("es-CO", { maximumFractionDigits: 0 });

/** What was typed -> whole pesos: only the digits count, so `5.000.000`, `5000000` and `$ 5.000.000` agree. */
const pesosEscritos = (texto) => {
  const digitos = String(texto || "").replace(/\D/g, "");
  return digitos ? Number(digitos) : null;
};

/** A list as it stands plus what is typed and not added yet — what "Guardar" sends. */
const listaFinal = (correos, escrito) => [...new Set([...correos, ...correosEscritos(escrito).correos])];

/** "Caja" out of "Diferencia grave en caja" — the card's heading already says the rest. */
const etiquetaCorta = (label) => {
  const corta = String(label || "").replace(/^diferencia grave en\s+/i, "");
  return corta ? corta.charAt(0).toUpperCase() + corta.slice(1) : label;
};

/** The longest message the backend keeps at the top of the urgent email. */
const MAX_MENSAJE = 1000;

const AYUDA_DIARIO =
  "Reciben el cierre del día cada vez que se procesa: cuántas oficinas cuadraron, las alertas y los documentos pendientes, " +
  "y la Matriz del día en Excel adjunta. También reciben las novedades cuando un cierre se vuelve a evaluar, y son a quienes va " +
  "\"Enviar consolidado\" si no se escribe otro correo.";

/** When the urgent email goes, naming the differences this client watches ("caja o bóveda"). */
const ayudaInmediato = (umbrales) => {
  const nombres = umbrales.map((t) => etiquetaCorta(t.label).toLowerCase());
  const cuales = nombres.length ? ` en ${nombres.join(" o ")}` : "";
  return (
    `Se envía apenas una oficina tiene una diferencia${cuales} que pasa el monto de aquí, sin esperar el resumen del día. ` +
    "Cada alerta se avisa una sola vez; si el monto cambia, se avisa de nuevo. No trae adjuntos."
  );
};

function Tarjeta({ titulo, subtitulo, ayuda, alinear = "start", children, guiaTour }) {
  return (
    <section {...guiaTour} className="flex flex-col gap-3 rounded-[10px] border bg-card p-3.5">
      <header>
        <div className="flex items-center gap-1.5">
          <h3 className="text-[13.5px] font-semibold text-brinks-foreground">{titulo}</h3>
          {ayuda ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="shrink-0 text-brinks-muted-foreground hover:text-brinks-foreground"
                  aria-label={`Qué recibe quien está en ${titulo}`}
                >
                  <Info className="h-3.5 w-3.5" />
                </button>
              </TooltipTrigger>
              {/* Below the title: above it, the dialog's top edge cuts the first line. Opened toward the
                  dialog's middle (`alinear`), so the right card's does not spill past its edge. */}
              <TooltipContent side="bottom" align={alinear} className="brinks-scope font-redhat max-w-[280px] text-[12px]">
                {ayuda}
              </TooltipContent>
            </Tooltip>
          ) : null}
        </div>
        <p className="text-[11.5px] text-brinks-muted-foreground">{subtitulo}</p>
      </header>
      {children}
    </section>
  );
}

function Correos({ id, quienes, correos, escrito, onEscrito, onCambio, deshabilitado }) {
  const { correos: nuevos, invalidos } = correosEscritos(escrito);

  const agregar = () => {
    if (!nuevos.length || invalidos.length) return;
    onCambio([...new Set([...correos, ...nuevos])]);
    onEscrito("");
  };

  return (
    <div className="grid gap-2">
      <div className="flex gap-2">
        <input
          id={id}
          type="text"
          value={escrito}
          disabled={deshabilitado}
          onChange={(e) => onEscrito(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              agregar();
            }
          }}
          placeholder="correo@empresa.com"
          aria-label={`Agregar correo a ${quienes}`}
          className={INPUT}
        />
        {!deshabilitado ? (
          <Button
            variant="outline"
            className="h-9 w-9 shrink-0 p-0"
            aria-label="Agregar"
            title="Agregar"
            disabled={!nuevos.length || invalidos.length > 0}
            onClick={agregar}
          >
            <Plus className="h-4 w-4" />
          </Button>
        ) : null}
      </div>
      {invalidos.length ? (
        <span className="text-[11.5px] text-brinks-danger">No es un correo válido: {invalidos.join(", ")}</span>
      ) : null}
      {correos.length ? (
        <ul className="flex flex-wrap gap-1.5" aria-label={`Reciben ${quienes}`}>
          {correos.map((correo) => (
            <li
              key={correo}
              className="inline-flex max-w-full items-center gap-1 rounded-full border bg-brinks-surface py-0.5 pl-2.5 pr-1 text-[12px] text-brinks-foreground"
            >
              <span className="truncate">{correo}</span>
              {!deshabilitado ? (
                <button
                  type="button"
                  className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-brinks-muted-foreground hover:bg-brinks-surface-hover hover:text-brinks-danger"
                  aria-label={`Quitar ${correo}`}
                  title="Quitar"
                  onClick={() => onCambio(correos.filter((c) => c !== correo))}
                >
                  <X className="h-3 w-3" />
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-[12px] text-brinks-muted-foreground">Sin correos.</p>
      )}
    </div>
  );
}

export function ConfiguracionAlertas({ open, token, mantisBusinessId, clientCode, onClose }) {
  const [cargando, setCargando] = useState(false);
  const [errorCarga, setErrorCarga] = useState(null);
  const [config, setConfig] = useState(null);
  const [diario, setDiario] = useState([]);
  const [inmediato, setInmediato] = useState([]);
  const [escritoDiario, setEscritoDiario] = useState("");
  const [escritoInmediato, setEscritoInmediato] = useState("");
  const [montos, setMontos] = useState({});
  const [mensaje, setMensaje] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);
  const [guardado, setGuardado] = useState(false);
  const [vista, setVista] = useState(null);
  const [cargandoVista, setCargandoVista] = useState(false);
  const [errorVista, setErrorVista] = useState(null);

  // What the server holds — on opening, and again after a save, so the lists show the addresses as
  // they were stored (lowercased, without repeats) rather than as they were typed.
  const mostrar = (data) => {
    setConfig(data);
    setDiario(data.recipients || []);
    setInmediato(data.urgent?.recipients || []);
    setMensaje(data.urgent?.message || "");
    setEscritoDiario("");
    setEscritoInmediato("");
    setMontos(Object.fromEntries((data.thresholds || []).map((t) => [t.ruleKey, formatoPesos(t.amount)])));
  };

  useEffect(() => {
    if (!open) return undefined;
    let vigente = true;
    (async () => {
      setCargando(true);
      setErrorCarga(null);
      setError(null);
      setGuardado(false);
      setVista(null);
      setErrorVista(null);
      const response = await getCierreNotificationSettings(token, mantisBusinessId, clientCode).catch(() => null);
      if (!vigente) return;
      setCargando(false);
      if (!response?.success) {
        setErrorCarga(errorDe(response, "No se pudo cargar la configuración de alertas."));
        return;
      }
      mostrar(response.data);
    })();
    return () => {
      vigente = false;
    };
  }, [open, token, mantisBusinessId, clientCode]);

  const puedeEditar = Boolean(config?.canEdit);
  const deshabilitado = !puedeEditar || guardando;
  const finalDiario = listaFinal(diario, escritoDiario);
  const finalInmediato = listaFinal(inmediato, escritoInmediato);
  const hayInvalidos =
    correosEscritos(escritoDiario).invalidos.length > 0 || correosEscritos(escritoInmediato).invalidos.length > 0;
  const umbrales = config?.thresholds || [];
  const montoInvalido = umbrales.some((t) => !(pesosEscritos(montos[t.ruleKey]) > 0));
  const hayAvisoInmediato = (config?.urgentAlerts || []).length > 0;

  // The message as typed, saved or not: the preview is how the email would read with it.
  const verVistaPrevia = async () => {
    setCargandoVista(true);
    setErrorVista(null);
    const response = await previewCierreUrgentEmail(token, mantisBusinessId, clientCode, mensaje).catch(() => null);
    setCargandoVista(false);
    if (response?.success && response.data?.available !== false) {
      setVista(response.data);
    } else {
      setVista(null);
      setErrorVista(
        response?.success
          ? "Este cliente no tiene aviso inmediato."
          : errorDe(response, "No se pudo cargar la vista previa del correo."),
      );
    }
  };

  const guardar = async () => {
    setGuardando(true);
    setError(null);
    setGuardado(false);
    const response = await saveCierreNotificationSettings(token, mantisBusinessId, clientCode, {
      recipients: finalDiario,
      urgent: { recipients: finalInmediato, message: mensaje.trim() || null },
      thresholds: Object.fromEntries(umbrales.map((t) => [t.ruleKey, pesosEscritos(montos[t.ruleKey])])),
    }).catch(() => null);
    setGuardando(false);
    if (response?.success) {
      mostrar(response.data);
      setGuardado(true);
    } else {
      setError(errorDe(response, "No se pudo guardar la configuración de alertas."));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(abierto) => !abierto && !guardando && onClose()}>
      <DialogContent className="brinks-scope font-redhat max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader className="text-left">
          <div className="flex min-w-0 items-center gap-1">
            <DialogTitle className="text-[16px] font-semibold text-brinks-foreground">Configuración de alertas</DialogTitle>
            <BotonGuia guiaId="dialogo-configuracion-alertas" titulo="Configuración de alertas" texto="Quién recibe los correos de alertas de este cliente y desde qué monto una diferencia se avisa de inmediato." />
          </div>
          <DialogDescription className="text-[12.5px] text-brinks-muted-foreground">
            Quién recibe por correo las alertas de este cliente.
          </DialogDescription>
        </DialogHeader>

        {cargando ? (
          <div className="flex items-center gap-2 py-6 text-[12.5px] text-brinks-muted-foreground">
            <Spinner size="sm" /> Cargando…
          </div>
        ) : errorCarga ? (
          <p role="alert" className="text-[12px] text-brinks-danger">
            {errorCarga}
          </p>
        ) : config ? (
          <div className="grid gap-3">
            {!puedeEditar ? (
              <p role="status" className="text-[12px] text-brinks-muted-foreground">
                Solo un administrador del cliente puede cambiar esta configuración.
              </p>
            ) : null}

            <TooltipProvider delayDuration={150}>
              <div className="grid gap-3 md:grid-cols-2">
                <Tarjeta guiaTour={guia("Resumen diario", "Los correos que reciben cada día el cierre del día con la Matriz adjunta. Escribe un correo y toca \"Agregar\"; la papelera lo quita.")} titulo="Resumen diario" subtitulo="El cierre del día con la Matriz adjunta." ayuda={AYUDA_DIARIO}>
                  <Correos
                    id="correos-resumen-diario"
                    quienes="el resumen diario"
                    correos={diario}
                    escrito={escritoDiario}
                    onEscrito={setEscritoDiario}
                    onCambio={setDiario}
                    deshabilitado={deshabilitado}
                  />
                </Tarjeta>

                <Tarjeta
                  guiaTour={guia("Aviso inmediato", "Los correos que reciben un aviso apenas una diferencia pasa del monto que pongas aquí, con el mensaje que quieras al inicio. \"Vista previa\" muestra cómo llega.")}
                  titulo="Aviso inmediato"
                  subtitulo="Apenas una diferencia pasa del monto."
                  ayuda={ayudaInmediato(umbrales)}
                  alinear="end"
                >
                  {umbrales.length ? (
                    <div className="grid gap-1.5">
                      {umbrales.map((t) => {
                        const valido = pesosEscritos(montos[t.ruleKey]) > 0;
                        return (
                          <div key={t.ruleKey} className="flex items-center gap-2">
                            <label htmlFor={`monto-${t.ruleKey}`} className="w-16 shrink-0 text-[12.5px] text-brinks-foreground" title={t.label}>
                              {etiquetaCorta(t.label)}
                            </label>
                            <span className="text-[12.5px] text-brinks-muted-foreground">± $</span>
                            <input
                              id={`monto-${t.ruleKey}`}
                              type="text"
                              inputMode="numeric"
                              aria-label={t.label}
                              aria-invalid={!valido}
                              title={t.defaultAmount ? `Por defecto: $${formatoPesos(t.defaultAmount)}` : undefined}
                              value={montos[t.ruleKey] ?? ""}
                              disabled={deshabilitado}
                              onChange={(e) => setMontos((m) => ({ ...m, [t.ruleKey]: e.target.value }))}
                              onBlur={() => setMontos((m) => ({ ...m, [t.ruleKey]: formatoPesos(pesosEscritos(m[t.ruleKey])) }))}
                              className={cn(INPUT, "text-right tabular-nums", !valido && "border-brinks-danger")}
                            />
                          </div>
                        );
                      })}
                      {montoInvalido ? (
                        <span className="text-[11.5px] text-brinks-danger">Escribe un monto mayor que cero.</span>
                      ) : null}
                    </div>
                  ) : null}
                  <Correos
                    id="correos-aviso-inmediato"
                    quienes="el aviso inmediato"
                    correos={inmediato}
                    escrito={escritoInmediato}
                    onEscrito={setEscritoInmediato}
                    onCambio={setInmediato}
                    deshabilitado={deshabilitado}
                  />
                  <div className="grid gap-1">
                    <label htmlFor="mensaje-aviso-inmediato" className="text-[12px] text-brinks-foreground">
                      Mensaje en el correo (opcional)
                    </label>
                    <textarea
                      id="mensaje-aviso-inmediato"
                      rows={3}
                      maxLength={MAX_MENSAJE}
                      value={mensaje}
                      disabled={deshabilitado}
                      onChange={(e) => setMensaje(e.target.value)}
                      placeholder="Va al inicio del correo, antes de las diferencias."
                      className={cn(INPUT, "h-auto resize-y py-2")}
                    />
                  </div>
                  {hayAvisoInmediato ? (
                    <Button
                      variant="outline"
                      className="h-8 w-fit gap-1.5 text-[12px]"
                      disabled={cargandoVista}
                      onClick={verVistaPrevia}
                    >
                      {cargandoVista ? <Spinner size="sm" /> : <Eye className="h-3.5 w-3.5" />} Vista previa
                    </Button>
                  ) : null}
                </Tarjeta>
              </div>
            </TooltipProvider>

            {errorVista ? (
              <p role="alert" className="text-[12px] text-brinks-danger">
                {errorVista}
              </p>
            ) : null}
            {vista ? (
              <section {...guia("Vista previa", "Así llega el aviso inmediato, sobre una oficina de ejemplo y con tu mensaje.")} aria-label="Vista previa del aviso inmediato" className="grid gap-2 rounded-[10px] border bg-card p-3.5">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-[12.5px] font-semibold text-brinks-foreground">{vista.subject}</p>
                    <p className="text-[11.5px] text-brinks-muted-foreground">
                      Ejemplo con datos ficticios: así llega el aviso inmediato a quien esté en la lista.
                    </p>
                  </div>
                  <Button variant="outline" className="h-8 shrink-0 text-[12px]" onClick={() => setVista(null)}>
                    Cerrar vista previa
                  </Button>
                </div>
                {/* `sandbox` with nothing allowed: an email body has no reason to run anything here. */}
                <iframe
                  title="Vista previa del correo"
                  sandbox=""
                  srcDoc={vista.html}
                  className="h-80 w-full rounded-[6px] border bg-white"
                />
              </section>
            ) : null}
          </div>
        ) : null}

        {error ? (
          <p role="alert" className="text-[12px] text-brinks-danger">
            {error}
          </p>
        ) : null}

        <DialogFooter className="items-center gap-2">
          <div className="flex items-center gap-2">
            {guardado ? (
              <span role="status" className="text-[12px] text-brinks-success">
                Configuración guardada.
              </span>
            ) : null}
            <Button variant="outline" className="h-9" disabled={guardando} onClick={onClose}>
              {puedeEditar ? "Cancelar" : "Cerrar"}
            </Button>
            {puedeEditar ? (
              <Button {...guia("Guardar", "Nada cambia hasta que guardas. Un correo escrito y no agregado también se guarda.")} className="h-9 gap-1.5" disabled={guardando || hayInvalidos || montoInvalido} onClick={guardar}>
                {guardando ? <Spinner size="sm" /> : <Mail className="h-4 w-4" />} Guardar
              </Button>
            ) : null}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
